import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';

// Page scroll lock shared by every overlay (modals, the mobile nav drawer). Counted,
// not save/restore: with stacked overlays closing out of order, restoring a saved
// value left the page stuck with scrolling disabled (pager unreachable).
let scrollLockCount = 0;

export const lockScroll = () => {
    scrollLockCount += 1;
    document.body.style.overflow = 'hidden';
};

export const unlockScroll = () => {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) document.body.style.overflow = '';
};

/**
 * Centered overlay dialog. Escape and the close button call onClose.
 * @param {string} [role='dialog'] 'alertdialog' for confirmations
 * @param {string} [describedBy] id of the element that describes the dialog
 * @param {React.Ref} [panelRef] ref to the dialog panel (focus management)
 */
const Modal = ({ isOpen, onClose, title, children, size = 'md', footer, role = 'dialog', describedBy, panelRef }) => {
    const titleId = useId();
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        full: 'max-w-[min(1440px,96vw)]',
    };
    // Large settings modals get a fixed height so the body scrolls and the footer stays on screen.
    const heightClass = size === 'full' ? 'h-[calc(100dvh-2rem)]' : 'max-h-[calc(100dvh-2rem)]';

    useEffect(() => {
        if (!isOpen) return undefined;

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
            }
        };

        lockScroll();

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            unlockScroll();
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    // Portal to <body> so no ancestor (transform, filter, overflow) can reposition or clip the overlay.
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div
                ref={panelRef}
                role={role}
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={describedBy}
                className={`w-full ${sizeClasses[size]} bg-white rounded-2xl shadow-xl border border-slate-200 ${heightClass} overflow-hidden flex flex-col`}
            >
                <div className="flex flex-shrink-0 items-center justify-between px-6 py-4 border-b border-slate-200">
                    <h2 id={titleId} className="text-lg font-semibold text-slate-900">
                        {title}
                    </h2>
                    <Tooltip label="Close">
                        <button
                            type="button"
                            onClick={onClose}
                            aria-label="Close"
                            className="p-1 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-900"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </Tooltip>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
                {footer && <div className="flex-shrink-0 border-t border-slate-200 bg-white px-6 py-4">{footer}</div>}
            </div>
        </div>,
        document.body
    );
};

export default Modal;
